import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { CalloutNodeView } from './CalloutNodeView';
import { createPreventNodeDeletionPlugin } from './plugins/PreventNodeDeletionPlugin';

/**
 * CalloutNode - A container node with a styled card appearance.
 *
 * Used to wrap important content (like goals reminders) in a visually distinct
 * section with a light green background. Supports a header attribute for displaying
 * a title within the callout.
 *
 * Structure:
 * - Header (from `header` attribute)
 * - Child content (block nodes like TaskList, goalsNode, etc.)
 */
export const CalloutNode = Node.create({
  name: 'calloutNode',
  group: 'block',
  content: 'block*', // Container - can hold any block content
  isolating: true, // Prevents content from being joined across the node boundary (e.g., backspace merging)

  addAttributes() {
    return {
      header: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-header') || '',
        renderHTML: (attributes) => {
          if (!attributes.header) return {};
          return {
            'data-header': attributes.header,
          };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="callout-node"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'callout-node',
        style:
          'background-color: #F0FFF4; border-radius: 8px; padding: 16px; margin: 8px 0;',
      }),
      0, // Content placeholder for child nodes
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CalloutNodeView);
  },

  addProseMirrorPlugins() {
    return [
      createPreventNodeDeletionPlugin({
        nodeNames: ['calloutNode'],
        allowEdits: true, // Allow editing content inside
      }),
    ];
  },
});
